import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ManagementApiService } from 'src/app/services';
import { GlobalEmittersService } from 'src/app/services/global-emitters.service';
import { DataService } from '../../../../services/data.service';
import { TighteningMethod } from '../../enums/tightening-method.enum';
import { DiagramInfo } from '../../models/diagram-info';
import { TighteningData } from '../../models/tightening-data';
import { Router } from '@angular/router';
import { pipeSpecVersion } from 'src/app/Modules/settings/models/idms/pipe-specification.model';
import { IdmsSharedService } from 'src/app/Modules/idms/services/idms-shared.service';
import { CalculationType } from '../../enums/calculation-type.enum';
import { JointCalcService } from 'src/app/Modules/idms/components/plant-manager/services/joint-calc-service.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-tightening-procedure-input',
    templateUrl: './tightening-procedure-input.component.html',
    styleUrls: ['./tightening-procedure-input.component.scss'],
})
export class TighteningProcedureInputComponent implements OnInit, OnChanges, OnDestroy {
    @Input() cssClass;
    @Input() isRedirect: boolean;
    @Input() pipeSpecVersion: pipeSpecVersion;
    @Input() isInitialFormLoad: boolean;
    @Input() enable: boolean;
    @Input() calculationType: string | number;

    @Output() processconfirm = new EventEmitter();
    @Output() diagram: EventEmitter<DiagramInfo> = new EventEmitter();
    @Output() returnData: EventEmitter<TighteningData> = new EventEmitter();
    destroy$ = new Subject();

    tooltip_position = 'above';
    calcMethodDefault: string = '';
    isBreakoutDefault: boolean = false;
    percentageCoverDefault: any = 100;
    lubricantDefault: string = '';
    lubricantTypeDefault: string = '0';
    lubricantValueDefault: number;
    tighteningFormGroup: UntypedFormGroup;
    tighteningData: TighteningData;
    _calculationType: string | number;
    constructor(
        private fb: UntypedFormBuilder,
        private dataSvc: DataService,
        private managmentService: ManagementApiService,
        private globalEmittersService: GlobalEmittersService,
        public router: Router,
        public idmsSharedService: IdmsSharedService,
        private jointCalcService: JointCalcService
    ) {}

    ngOnInit() {
        this.globalEmittersService.isUserDefaultsLoaded.pipe(takeUntil(this.destroy$)).subscribe((res) => {
            if (res) {
                this.managmentService.userDefaults.forEach((u) => {
                    if (u.userPreferenceKey === 'DefaultTighteningMethod') {
                        this.calcMethodDefault = u.userPreference;
                        if (this.calculationType == CalculationType.Clamp) {
                            this.calcMethodDefault = 'Torque';
                        }
                    }
                });
            }
        });
        if (this.router.url == '/informate/clamp/false' || this.router.url == '/informate/clamp/true' || this.calculationType == CalculationType.Clamp) {
            this.calcMethodDefault = 'Torque';
        }
        this.tighteningData = new TighteningData();
        this.loadFormGroup();
        this.tighteningFormGroup = this.fb.group({
            calcMethod: [this.calcMethodDefault, Validators.required],
            // isBreakout: [this.isBreakoutDefault],
            percentageCover: [this.percentageCoverDefault],
            lubricant: [this.lubricantDefault, Validators.required],
            lubricantType: [this.lubricantTypeDefault, Validators.required],
            lubricantValue: [this.lubricantValueDefault, [Validators.required, Validators.min(0.01), Validators.max(0.99)]],
        });
        this.tighteningFormGroup.markAllAsTouched();

        this.globalEmittersService.tighteningProcedureForm.next(this.tighteningFormGroup);

        this.tighteningFormGroup
            .get('calcMethod')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
                if (value) {
                    (this.calcMethodDefault = value), this.emitDiagramEvent();
                    this.dataSvc.selectedToolId = null;
                    this.dataSvc.selectedToolName = null;
                    this.globalEmittersService.isCalculationHappen.next(false);
                }
            });

        this.tighteningFormGroup
            .get('percentageCover')
            .valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value) => [(this.percentageCoverDefault = value), this.emitDiagramEvent()]);
        if (this.router.url == '/idms/plantmanager' || this.router.url.includes('/idms/plantmanagerView') || this.router.url.includes('/settings/idms')) {
            // Subscribe to valueChanges and emit the form when it changes
            this.tighteningFormGroup.valueChanges.subscribe((formValue) => {
                this.btnCtpConfirmClicked();
            });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.calcMethodDefault == 'Tension' && !this.isInitialFormLoad) {
            this.emitDiagramEvent();
        }
    }

    loadFormGroup() {
        if (this.isRedirect && this.dataSvc.calculationData != null) {
            this.initializeDefaults(
                this.dataSvc.calculationData.input.others,
                this.dataSvc.calculationData.input.recordId,
                this.dataSvc.calculationData.input.calculationOverrides
            );
        }

        if (this.idmsSharedService.enableConfirmWithRouter(2) && this.pipeSpecVersion) {
            this.initializeDefaults(this.pipeSpecVersion?.others, this.pipeSpecVersion?.recordId, this.pipeSpecVersion?.calculationOverrides);
        }

        if (this.jointCalcService.pipeSpecList?.length) {
            const lastPipeSpec = [...this.jointCalcService.pipeSpecList][this.jointCalcService.pipeSpecList.length - 1];
            this.initializeDefaults(lastPipeSpec?.others, lastPipeSpec?.recordId, lastPipeSpec?.calculationOverrides);
        }
    }

    private initializeDefaults(others: any, recordId: any, calculationOverrides: any) {
        if (!others) return;

        this.calcMethodDefault = others.tighteningMethod == TighteningMethod.Torque ? 'Torque' : 'Tension';

        if ((this.router.url == '/settings/idms' || this.router.url == '/idms/plantmanager') && this.calculationType == CalculationType.Clamp) {
            this.calcMethodDefault = 'Torque';
        }

        if (others.tighteningMethod == TighteningMethod.Torque) {
            this.isBreakoutDefault = others.breakout;
        } else if (others.tighteningMethod == TighteningMethod.Tension) {
            this.percentageCoverDefault = this.mapPercentageCover(others.percentageCover);
        }

        this.lubricantDefault = recordId?.lubricantId;

        if (others.lubricantMode == '1') {
            this.lubricantTypeDefault = '1';
            this.lubricantValueDefault = calculationOverrides?.kFactor;
        }

        this.emitDiagramEvent();
    }

    private mapPercentageCover(percentageCover: string | number): number {
        const percentageCoverString = typeof percentageCover === 'string' ? percentageCover : String(percentageCover);

        switch (percentageCoverString) {
            case '0.25':
                return 25;
            case '0.33':
                return 33;
            case '0.50':
            case '0.5':
                return 50;
            default:
                return 100;
        }
    }

    emitDiagramEvent() {
        let diagramInfo = new DiagramInfo();
        diagramInfo.diagramType = 'Bolt Setting';
        if (this.calcMethodDefault !== null && this.calcMethodDefault === 'Tension') {
            diagramInfo.name = this.percentageCoverDefault === 100 ? '100%' : '50%';
        } else {
            diagramInfo.name = null;
            this.globalEmittersService.customInputForm.pipe(takeUntil(this.destroy$)).subscribe((form: UntypedFormGroup) => {
                if (form) {
                    let boltQuantity = form.get('boltQuantity').value;
                    if (boltQuantity) {
                        diagramInfo.diagramType = 'Torque Sequence';
                        diagramInfo.noOfBolts = form.get('boltQuantity')?.value;
                    }
                }
            });
        }
        this.diagram.emit(diagramInfo);
    }

    btnCtpConfirmClicked(): void {
        this.tighteningFormGroup.markAllAsTouched();
        if (this.tighteningFormGroup.valid) {
            this.tighteningData.tighteningMethod = <any>TighteningMethod[this.tighteningFormGroup.get('calcMethod').value];
            if (this.tighteningData.tighteningMethod == TighteningMethod.Torque) {
                // this.tighteningData.breakout = this.tighteningFormGroup.get("isBreakout").value;
            } else {
                switch (this.tighteningFormGroup.get('percentageCover').value) {
                    case 25:
                        this.tighteningData.percentageCover = '0.25';
                        break;
                    case 33:
                        this.tighteningData.percentageCover = '0.33';
                        break;
                    case 50:
                        this.tighteningData.percentageCover = '0.50';
                        break;
                    default:
                        this.tighteningData.percentageCover = '1';
                        break;
                }
            }

            this.tighteningData.lubricantId = this.tighteningFormGroup.get('lubricant').value;

            this.tighteningData.lubricantMode = this.tighteningFormGroup.get('lubricantType').value;

            if (this.tighteningData.lubricantMode == '1') {
                this.tighteningData.kFactor = this.tighteningFormGroup.get('lubricantValue').value;
            }
            this.tighteningData.isDataValid = true;
            this.returnData.emit(this.tighteningData);
        } else {
            this.tighteningData.isDataValid = false;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
